const _excluded = ["onClick"],
  _excluded2 = ["renderEndSlot", "renderStartSlot", "paddingStart", "paddingEnd", "paddingTop", "paddingBottom", "separator", "variant", "highlighted", "disabled", "selected", "selectionMode"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React from "react";
import Check from "@bolteu/kalep-react-icons/dist/Check";
import { Checkbox } from "../Checkbox";
import { Radio } from "../RadioGroup";
import { Switch } from "../Switch";
import { TypographyStack } from "../Typography/TypographyStack";
import { cx } from "../../helpers/utils/cx";
import { noopNull } from "../../helpers/utils/noop";
const variants = {
  base: "py-3 gap-3 min-h-12",
  sm: "py-[6px] gap-3 min-h-9"
};
export const ListItemLayout = /*#__PURE__*/React.forwardRef(function ListItemLayoutComponent(_ref, ref) {
  let {
      onClick
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const {
      renderEndSlot = noopNull,
      renderStartSlot = noopNull,
      paddingStart = 4,
      paddingEnd = 4,
      paddingTop,
      paddingBottom,
      separator = false,
      variant = "base",
      highlighted = false,
      disabled = false,
      selected,
      selectionMode = "solo-checkmark"
    } = props,
    rest = _objectWithoutProperties(props, _excluded2);
  const isClickable = typeof onClick === "function";
  const isHighlighted = highlighted && !disabled;
  const hasSelectionSlot = selected !== undefined;
  const selectionSlotPosition = selectionMode === "solo-radio" || selectionMode === "multiple" ? "start" : "end";
  const handleKeyDown = React.useCallback(event => {
    if (isClickable && !disabled && (event.key === "Enter" || event.key === " ")) {
      onClick();
    }
  }, [isClickable, disabled, onClick]);
  const handleOnClick = React.useCallback(_event => {
    if (isClickable && !disabled) {
      onClick();
    }
  }, [isClickable, disabled, onClick]);
  const containerProps = React.useMemo(() => {
    if (isClickable) {
      return {
        onClick: handleOnClick,
        onKeyDown: handleKeyDown,
        role: "button",
        tabIndex: 0,
        "aria-disabled": disabled
      };
    }
    return {};
  }, [isClickable, disabled, handleOnClick, handleKeyDown]);
  const typographyStackProps = React.useMemo(() => {
    return disabled ? _objectSpread(_objectSpread({}, rest), {}, {
      primaryTypographyProps: {
        color: "tertiary"
      },
      secondaryTypographyProps: {
        color: "tertiary"
      }
    }) : rest;
  }, [disabled, rest]);
  const leftSlot = React.useMemo(() => {
    return renderStartSlot({
      disabled,
      size: mapVariantToSize(variant)
    }, noopNull);
  }, [disabled, variant, renderStartSlot]);
  const rightSlot = React.useMemo(() => {
    return renderEndSlot({
      disabled,
      size: mapVariantToSize(variant)
    }, noopNull);
  }, [disabled, variant, renderEndSlot]);
  const outerContainerStyle = React.useMemo(() => {
    return {
      paddingInlineStart: getRemFromSpacingSize(paddingStart),
      paddingInlineEnd: getRemFromSpacingSize(paddingEnd)
    };
  }, [paddingStart, paddingEnd]);
  const innerContainerStyle = React.useMemo(() => {
    return {
      paddingTop: typeof paddingTop === "number" ? getRemFromSpacingSize(paddingTop) : undefined,
      paddingBottom: typeof paddingBottom === "number" ? getRemFromSpacingSize(paddingBottom) : undefined
    };
  }, [paddingTop, paddingBottom]);
  return /*#__PURE__*/React.createElement("div", {
    style: outerContainerStyle,
    ref: ref,
    className: cx("w-full", isClickable && "hover:bg-neutral-secondary focus:hover:bg-neutral-secondary", isHighlighted && "bg-neutral-secondary", isClickable && "cursor-pointer", isClickable && disabled && "cursor-not-allowed")
  }, /*#__PURE__*/React.createElement("div", _extends({
    className: cx("flex items-center", variants[variant], separator && "border-solid border-0 border-separator border-b"),
    style: innerContainerStyle
  }, containerProps), hasSelectionSlot && selectionSlotPosition === "start" ? /*#__PURE__*/React.createElement(SelectionSlot, {
    selected: selected,
    disabled: disabled,
    selectionMode: selectionMode,
    "aria-label": rest["aria-label"]
  }) : null, leftSlot ? /*#__PURE__*/React.createElement("div", {
    className: cx("shrink-0 h-full flex items-center")
  }, leftSlot) : null, /*#__PURE__*/React.createElement("div", {
    className: cx("flex-1")
  }, /*#__PURE__*/React.createElement(TypographyStack, _extends({
    variant: variant
  }, typographyStackProps))), rightSlot ? /*#__PURE__*/React.createElement("div", {
    className: cx("shrink-0 ml-auto h-full flex items-center")
  }, rightSlot) : null, hasSelectionSlot && selectionSlotPosition === "end" ? /*#__PURE__*/React.createElement(SelectionSlot, {
    selected: selected,
    disabled: disabled,
    selectionMode: selectionMode,
    "aria-label": rest["aria-label"]
  }) : null));
});
function mapVariantToSize() {
  let variant = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "base";
  return variant === "base" ? "md" : "sm";
}
function getRemFromSpacingSize(spacingValue) {
  return `${spacingValue / 4}rem`;
}
function SelectionSlot(_ref2) {
  let {
    selected,
    disabled,
    selectionMode = "solo-checkmark",
    "aria-label": ariaLabel
  } = _ref2;
  switch (selectionMode) {
    case "solo-radio":
      {
        return /*#__PURE__*/React.createElement("div", {
          className: cx("shrink-0 h-full flex items-center")
        }, /*#__PURE__*/React.createElement(Radio, {
          checked: selected,
          disabled: disabled,
          readOnly: true,
          "aria-label": ariaLabel,
          tabIndex: -1
        }));
      }
    case "solo-checkmark":
      {
        return selected ? /*#__PURE__*/React.createElement("div", {
          className: cx("ml-auto shrink-0 h-full flex items-center")
        }, /*#__PURE__*/React.createElement(Check, {
          size: "md",
          className: cx(disabled ? "text-tertiary" : "text-action-secondary"),
          "aria-label": ariaLabel
        })) : null;
      }
    case "multiple":
      {
        return /*#__PURE__*/React.createElement("div", {
          className: cx("shrink-0 h-full flex items-center")
        }, /*#__PURE__*/React.createElement(Checkbox, {
          checked: selected,
          disabled: disabled,
          readOnly: true,
          "aria-label": ariaLabel,
          tabIndex: -1
        }));
      }
    case "switch":
      {
        return /*#__PURE__*/React.createElement("div", {
          className: cx("ml-auto shrink-0 h-full flex items-center")
        }, /*#__PURE__*/React.createElement(Switch, {
          checked: selected,
          disabled: disabled,
          readOnly: true,
          "aria-label": ariaLabel,
          tabIndex: -1
        }));
      }
    default:
      {
        return null;
      }
  }
}